<template>
    <div id="voucherImageSelect">
        <media-only desktop>
            <ul class="voucher-picture-gallery">
                <li
                    class="gallery-item"
                    v-for="voucherImage in availableVoucherDesigns"
                    :key="'voucherImage' + voucherImage.id"
                >
                    <input
                        type="radio"
                        name="selectedVoucherImage"
                        class="item-select"
                        :id="'selectedVoucherImage-' + voucherImage.id"
                        :value="voucherImage.id"
                        :checked="isActive(voucherImage)"
                        @change="updateMethod(voucherImage.id)"
                    />
                    <label :for="'selectedVoucherImage-' + voucherImage.id" class="gallery-item-label" tabindex="0" @keydown.enter="updateMethod(voucherImage.id)" @keydown.space.prevent="updateMethod(voucherImage.id)">
                        <VoucherImageCard
                            :voucher-image="voucherImage"
                            :active="isActive(voucherImage)"
                            :key="'voucherImageDesktop' + voucherImage.id"
                        />
                    </label>
                </li>
            </ul>
        </media-only>

        <media-only mobile>
            <Swiper :options="swiperOptions" @slideChange="onSlideChange" @load="onSwiperLoad">
                <VoucherImageCard
                    v-for="voucherImage in availableVoucherDesigns"
                    :voucher-image="voucherImage"
                    :active="isActive(voucherImage)"
                    :key="'voucherImageMobile' + voucherImage.id"
                    class="swiper-slide"
                />
            </Swiper>
        </media-only>
    </div>
</template>

<script>
import VoucherImageCard from '@/components/checkout/VoucherImageCard';
import Swiper from '@/components/swiper/Swiper';

export default {
    name: 'VoucherImageSelect',
    components: {
        Swiper,
        VoucherImageCard
    },
    data() {
        const initialIndex = 0;
        return {
            initialIndex,
            swiperOptions: {
                loop: true,
                loopedSlides: 0,
                initialSlide: initialIndex,
                grabCursor: true,
                centeredSlides: true,
                effect: 'coverflow',
                slidesPerView: 'auto',
                coverflowEffect: {
                    rotate: 50,
                    stretch: 0,
                    depth: 100,
                    modifier: 1,
                    slideShadows: false //removed for slow devices better performance
                }
            }
        };
    },
    props: {
        //Backend provided voucher designs
        availableVoucherDesigns: {
            type: Array,
            default() {
                return [];
            }
        },
        selected: [String, Number]
    },
    methods: {
        updateMethod(id) {
            this.$emit('update:selected', id);
        },
        onSlideChange(swiper) {
            const id = this.getDesignId(swiper.realIndex);
            if (id != null) {
                this.updateMethod(this.availableVoucherDesigns[swiper.realIndex].id);
            }
        },
        isActive(voucher) {
            return this.selected == voucher.id;
        },
        getDesignId(index) {
            const id = this.$getSafe(this.availableVoucherDesigns, `[${index}].id`);
            console.assert(id != null, 'Cannot obtain voucher design id by index!');
            return id;
        },
        getIndexById(designId) {
            return this.availableVoucherDesigns.findIndex(d => d.id == designId);
        },
        onSwiperLoad(swiper) {
            const initialSlide = this.availableVoucherDesigns[swiper.realIndex];
            const initialSlideId = initialSlide && initialSlide.id;
            if (
                this.selected != null &&
                this.availableVoucherDesigns.find(d => d.id == this.selected) &&
                this.selected != initialSlideId
            ) {
                swiper.slideToLoop(this.getIndexById(this.selected));
            }
        }
    },
    watch: {
        //Preselecting the first voucher image
        availableVoucherDesigns: {
            handler(designs) {
                if (designs.length) {
                    let selectedId;

                    if (this.selected != null) {
                        const selectedDesign = designs.find(d => d.id == this.selected);
                        if (selectedDesign) {
                            selectedId = selectedDesign.id;
                        }
                    }

                    if (selectedId == null) {
                        selectedId = this.getDesignId(this.initialIndex);
                    }

                    this.updateMethod(selectedId);
                }
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;

@include breakpoints.media-breakpoint-down(variables.$mobile-breakpoint-max) {
    #voucherImageSelect {
        margin: 0 (-(variables.$grid-padding));
        max-height: 280px;
        overflow: hidden;
    }
}

.voucher-picture-gallery {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    grid-auto-rows: 1fr;
    margin: 0;
    padding: 0;
}

.gallery-item {
    list-style: none;
    margin: 0;
    padding: 0;

    .gallery-item-label {
        width: 100%;
    }
}

.item-select {
    position: absolute;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);

    & + label {
        height: 100%;
        margin: 0;
        padding: 0;
    }

    &:not(:checked) + label {
        cursor: pointer;
    }

    &:not(:checked):focus + label .item-card:after,
    &:not(:checked):hover + label .item-card:after {
        opacity: 1;
    }
}

/* --- Mobile swiper --- */
.swiper-slide {
    width: calc(100% - 90px);
    max-width: 300px;
    float: left;
    $self: &;
}

/* --- css grid hacked in Flexbox for IE11 --- */
@include variables.ie {
    .voucher-picture-gallery {
        display: flex;
        flex-wrap: wrap;
        margin: -1em;
        padding: 1em 0 0 1em;
    }
    .gallery-item {
        background-clip: padding-box;
        // fake grid gaps
        border: 0 solid transparent;
        border-width: 0 1em 1em 0;
        flex-basis: 31%;
        flex-grow: 0;
        flex-shrink: 0;

        .gallery-item-label {
            width: 100%;
        }
    }
}
</style>
